import { useMemo, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { resetVocabulary } from '@/api/vocabulary'
import { cn } from '@/utils/cn'
import type { ResetTarget, VocabularyEntry } from '@/types/vocabulary'
import type { Deck } from '@/types/deck'
import { focusRing, quietFill } from '@/components/ui/buttonStyles'

interface ResetVocabularyModalProps {
  languageId: number
  languageName: string
  entries: VocabularyEntry[]
  decks: Deck[]
  onClose: () => void
  onReset: () => void
}

const TARGET_ROWS: { value: ResetTarget; label: string; hint: string }[] = [
  {
    value: 'tracked_words',
    label: 'Tracked words',
    hint: 'Words you tracked while reading, with their review history',
  },
  {
    value: 'custom_words',
    label: 'Custom word cards',
    hint: 'Word cards you added yourself',
  },
  {
    value: 'phrases',
    label: 'Phrases',
    hint: 'Every phrase card — the decks themselves are kept',
  },
]

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
}

export function ResetVocabularyModal({
  languageId, languageName, entries, decks, onClose, onReset,
}: ResetVocabularyModalProps) {
  const [targets, setTargets] = useState<ResetTarget[]>([])
  const [deckIds, setDeckIds] = useState<number[]>([])
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const counts = useMemo<Record<ResetTarget, number>>(() => ({
    tracked_words: entries.filter((e) => e.type === 'word').length,
    custom_words: entries.filter((e) => e.type === 'custom' && e.card_type === 'word').length,
    phrases: entries.filter((e) => e.type === 'custom' && e.card_type === 'phrase').length,
  }), [entries])

  const cardTotal = useMemo(() => {
    const deckPhrases = targets.includes('phrases')
      ? counts.phrases
      : decks.filter((d) => deckIds.includes(d.id)).reduce((sum, d) => sum + d.card_count, 0)
    const words = targets.filter((t) => t !== 'phrases').reduce((sum, t) => sum + counts[t], 0)
    return words + deckPhrases
  }, [targets, deckIds, counts, decks])

  const nothingSelected = targets.length === 0 && deckIds.length === 0

  function select<T>(setter: (next: T[]) => void, list: T[], value: T) {
    setConfirming(false)
    setError(null)
    setter(toggle(list, value))
  }

  function handleReset() {
    if (!confirming) {
      setConfirming(true)
      return
    }
    setSubmitting(true)
    setError(null)
    resetVocabulary(languageId, targets, deckIds)
      .then(() => {
        onReset()
        onClose()
      })
      .catch(() => setError('Could not reset your vocabulary. Please try again.'))
      .finally(() => setSubmitting(false))
  }

  return (
    <Modal open onClose={onClose} title="Reset vocabulary">
      <p className="text-sm text-muted font-light leading-relaxed">
        Choose what to remove from your {languageName} vocabulary. This only affects your
        own progress, and it cannot be undone.
      </p>

      <fieldset className="mt-5 space-y-1">
        <legend className="text-xs uppercase tracking-widest text-label font-light">
          Your words
        </legend>
        {TARGET_ROWS.map(({ value, label, hint }) => (
          <CheckRow
            key={value}
            label={label}
            hint={hint}
            count={counts[value]}
            unit={value === 'phrases' ? 'phrases' : 'cards'}
            checked={targets.includes(value)}
            disabled={counts[value] === 0}
            onChange={() => select(setTargets, targets, value)}
          />
        ))}
      </fieldset>

      {decks.length > 0 && (
        <fieldset className="mt-5 space-y-1">
          <legend className="text-xs uppercase tracking-widest text-label font-light">
            Decks to delete
          </legend>
          {decks.map((deck) => (
            <CheckRow
              key={deck.id}
              label={deck.name}
              hint="Deletes the deck and every phrase in it"
              count={deck.card_count}
              unit="phrases"
              checked={deckIds.includes(deck.id)}
              onChange={() => select(setDeckIds, deckIds, deck.id)}
            />
          ))}
        </fieldset>
      )}

      <p className="mt-5 text-sm font-light text-body" aria-live="polite">
        {nothingSelected
          ? 'Nothing selected yet.'
          : `Deleting ${cardTotal} ${cardTotal === 1 ? 'card' : 'cards'}${
              deckIds.length > 0
                ? ` and ${deckIds.length} ${deckIds.length === 1 ? 'deck' : 'decks'}`
                : ''
            }.`}
      </p>

      {error && (
        <p role="alert" className="mt-3 text-xs text-error font-light">
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className={cn(
            'px-5 py-2.5 rounded-full text-sm font-normal tracking-wide min-h-11',
            'transition-colors duration-200',
            quietFill,
            focusRing,
          )}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={nothingSelected || submitting}
          className={cn(
            'px-5 py-2.5 rounded-full text-sm font-normal tracking-wide min-h-11',
            'transition-colors duration-200 disabled:opacity-40',
            'bg-btn-danger-bg text-btn-danger-text hover:bg-btn-danger-hover-bg',
            confirming && 'bg-btn-danger-confirm-bg hover:bg-btn-danger-confirm-hover-bg',
            focusRing,
          )}
        >
          {confirming ? 'Confirm — delete permanently' : 'Reset selected'}
        </button>
      </div>
    </Modal>
  )
}

function CheckRow({ label, hint, count, unit, checked, disabled, onChange }: {
  label: string
  hint: string
  count: number
  unit: string
  checked: boolean
  disabled?: boolean
  onChange: () => void
}) {
  return (
    <label
      className={cn(
        'flex items-start gap-3 rounded-lg px-3 py-3 min-h-11 cursor-pointer',
        'transition-colors duration-200',
        disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-surface-bg/50',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="mt-0.5 size-4 shrink-0 accent-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-light text-body truncate">{label}</span>
          <span className="shrink-0 text-xs text-muted font-light">
            {count} {unit}
          </span>
        </span>
        <span className="block mt-0.5 text-xs text-muted font-light leading-relaxed">
          {hint}
        </span>
      </span>
    </label>
  )
}
